import React, { PureComponent } from 'react'
import styled from 'styled-components'

import getPalette, { IPalette } from '../../helpers/colors/colors'
import getFont from '../../helpers/fonts/fonts'
import SettingsToggle from './components/SettingsToggle'
import SettingsCarousel from './components/SettingsCarousel'
import MainButton from '../../components/buttons/MainButton'
import { IGameSettings } from '../../helpers/data/gameSettings'
import settingsEnums from '../../helpers/enums/settings'
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg'
import { ReactComponent as RandomizeIcon } from '../../assets/icons/randomize.svg'
import { ReactComponent as SoundIcon } from '../../assets/icons/sound-icon.svg'
import { ReactComponent as TimerIcon } from '../../assets/icons/timer.svg'
import { Icon } from '@nearpod-packages/base-library'

const colors: IPalette = getPalette()

const AnimationsIcon: React.FC<{ className?: string }> = ({ className }) => (
	<Icon name="animation" size={16} className={className} />
)

const Wrapper = styled.div`
	width: 100%;
`

const Header = styled.div`
	width: 100%;
	text-align: center;
	padding-top: 12px;
`

const TitleContainer = styled.div`
	width: 100%;
	color: ${colors.grayBlue};
	font-family: ${getFont('primaryMedium')};
	font-size: 24px;
	padding-top: 10px;
`

const Content = styled.div`
	width: 100%;
	text-align: left;
	padding: 0 50px 20px;

	@media (min-width: ${600}px) {
		padding: 0 24px 0;
	}

	@media (min-width: ${800}px) {
		padding-bottom: 20px;
	}
`
const Paragraph = styled.p`
	width: 100%;
	color: ${colors.grayBlue};
	font-family: ${getFont('primaryMedium')};
	font-size: 14px;
	text-align: left;
	line-height: 17px;
	margin-bottom: 15px;
	margin-top: 24px;
`

const ButtonsContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	padding-right: 24px;
`

const ContinueButton = styled.div`
	width: 130px;
	display: flex;
	justify-content: flex-end;
	font-size: 14px;
	margin: 24px 0;
	text-align: center;
`

interface ISettingsState {
	options: any[]
}

interface ISettingsProps {
	settings: IGameSettings
	onSettingsSubmit: (settings: any) => void
}

class Settings extends PureComponent<ISettingsProps, ISettingsState> {
	constructor(props: any) {
		super(props)
		this.state = {
			options: this.initializeOptions(props.settings),
		}
	}

	initializeOptions(settings: any) {
		const audioLabelText = 'Play sound on all student devices'
		const isTimerDisabledSetting = settings.data.find((s: any) => s.id === 'isTimerDisabled')
		const isTimerLockedAndOn = isTimerDisabledSetting?.locked === true

		return settings.data.map((setting: any) => {
			let option: any = {}
			switch (setting.id) {
				case 'answersMode':
					option = {
						...setting,
						type: 'toggle',
						label: 'Randomize answers',
						values: settingsEnums.values[settingsEnums.SETTING_TYPE_ANSWERS_MODE],
						icon: RandomizeIcon,
					}
					break
				case 'skin':
					option = {
						...setting,
						type: 'carousel',
					}
					break
				case 'isPauseEnabled':
					option = {
						...setting,
						type: 'toggle',
						label: 'Pause game between questions for discussion',
						values: settingsEnums.values[settingsEnums.SETTING_TYPE_PAUSE],
						icon: PauseIcon,
						disabled: isTimerLockedAndOn,
					}
					break
				case 'audio':
					option = {
						...setting,
						type: 'toggle',
						label: audioLabelText,
						values: settingsEnums.values[settingsEnums.SETTING_TYPE_AUDIO],
						icon: SoundIcon,
					}
					break
				case 'isTimerDisabled':
					option = {
						...setting,
						type: 'toggle',
						label: 'Disable timers',
						values: settingsEnums.values[settingsEnums.SETTING_TYPE_TIMER_DISABLED],
						icon: TimerIcon,
						disabled: setting.locked || false,
					}
					break
				case 'areAnimationsEnabled':
					option = {
						...setting,
						type: 'toggle',
						label: 'Enable animations',
						values: settingsEnums.values[settingsEnums.SETTING_TYPE_ANIMATIONS],
						icon: AnimationsIcon,
					}
					break
			}
			return option
		})
	}

	onChange = (id: string, value: string | number) => {
		const index = this.state.options.findIndex((option) => option.id === id)
		if (index >= 0) {
			const option = this.state.options[index]
			let updatedOptions = [
				...this.state.options.slice(0, index),
				{
					...option,
					value,
				},
				...this.state.options.slice(index + 1),
			]

			if (id === 'isTimerDisabled' && value !== settingsEnums.SETTING_TIMER_DEFAULT) {
				const pauseIndex = updatedOptions.findIndex((opt) => opt.id === 'isPauseEnabled')
				if (pauseIndex >= 0) {
					updatedOptions = [
						...updatedOptions.slice(0, pauseIndex),
						{
							...updatedOptions[pauseIndex],
							value: settingsEnums.SETTING_GAME_CANT_PAUSE,
						},
						...updatedOptions.slice(pauseIndex + 1),
					]
				}
			} else if (id === 'isPauseEnabled' && value === settingsEnums.SETTING_GAME_CAN_PAUSE) {
				const timerIndex = updatedOptions.findIndex((opt) => opt.id === 'isTimerDisabled')
				if (timerIndex >= 0) {
					updatedOptions = [
						...updatedOptions.slice(0, timerIndex),
						{
							...updatedOptions[timerIndex],
							value: settingsEnums.SETTING_TIMER_DEFAULT,
						},
						...updatedOptions.slice(timerIndex + 1),
					]
				}
			}

			this.setState({
				options: updatedOptions,
			})
		}
	}

	buildCarouselOptions = (options: any[]) => {
		return options.map((option) => {
			switch (option.type) {
				case 'carousel':
					return (
						<SettingsCarousel
							key={option.id}
							value={option.value}
							onChange={this.onCarouselChange}
							values={option.values}
						/>
					)
				default:
					return null
			}
		})
	}

	buildToggleOptions = (options: any[]) => {
		return options.map((option) => {
			switch (option.type) {
				case 'toggle':
					const icon = <option.icon className='label__icon'></option.icon>
					return (
						<SettingsToggle
							key={option.id}
							id={option.id}
							value={option.value}
							onChange={this.onChange}
							label={option.label}
							values={option.values}
							icon={icon}
							disabled={option.disabled}
						/>
					)
				default:
					return null
			}
		})
	}

	onCarouselChange = (value: number) => {
		this.onChange(settingsEnums.SETTING_TYPE_SKIN, value)
	}

	onSubmit = () => {
		const selectedSettings = this.state.options.reduce((acc, option) => {
			acc[option.id] = option.value
			return acc
		}, {})

		this.props.onSettingsSubmit(selectedSettings)
	}

	render() {
		return (
			<Wrapper>
				<Header>
					<TitleContainer>Time to Climb Settings</TitleContainer>
				</Header>

				<Content>
					{this.buildCarouselOptions(this.state.options)}
					<Paragraph>
						<b>Note:</b> Time to Climb is a speed-based game. Students earn additional points for correct answers based
						on how quickly they answer them.
					</Paragraph>
					{this.buildToggleOptions(this.state.options)}
				</Content>

				<ButtonsContainer>
					<ContinueButton>
						<MainButton isRounded={true} text={'Continue'} onSubmit={this.onSubmit} />
					</ContinueButton>
				</ButtonsContainer>
			</Wrapper>
		)
	}
}

export default Settings
